import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ResponsiveTable, SkeletonLoader } from '@/components/ui';
import { Mail, Phone, Users, ArrowLeft, Pencil, Trash, Plus, Database, MessageCircle } from 'lucide-react';
import { ProtectedAdminRoute } from '@/components/admin/ProtectedAdminRoute';
import { useMembers as useMembersQuery } from '@/hooks/use-members';
import { useAuth } from '@/hooks/use-auth';
import { useLanguage } from '@/hooks/use-language';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useMessage } from '@/hooks/use-message';
import { useStartChat } from '@/hooks/use-start-chat';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { getGroupColorClasses, getAdminColorClasses, getLocalizedGroupName } from '@/utils/groupColors';
import { devLog } from '@/utils/console-utils';
import { CreateMemberDialog } from '@/components/members/CreateMemberDialog';
import { EditMemberDialog } from '@/components/members/EditMemberDialog';
import { DeleteMemberDialog } from '@/components/members/DeleteMemberDialog';
import { MemberAnalytics } from '@/components/admin/MemberAnalytics';
import { MemberExport } from '@/components/admin/MemberExport';
import { AdvancedMemberFilters } from '@/components/admin/AdvancedMemberFilters';
import { useAdminStats } from '@/hooks/use-admin-stats';
import { cn } from '@/lib/utils';

interface Member {
  id: number;
  name: string;
  phone: string;
  email: string;
  groups: string[];
  subgroups: string[];
  isAdmin: boolean;
  dbUserId?: string;
}

export default function Members() {
  const { user } = useAuth();
  const supabase = useSupabase();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { startChat: handleStartChat } = useStartChat();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const adminStats = useAdminStats();

  const [search, setSearch] = React.useState(params.get('q') || '');
  const [searchInput, setSearchInput] = React.useState(params.get('q') || '');
  const [page, setPage] = React.useState(() => {
    const p = parseInt(params.get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const pageSize = 10;

  const [isLoading, setIsLoading] = React.useState(true);
  const [members, setMembers] = React.useState<Member[]>([]);
  const [expanded, setExpanded] = React.useState<number | null>(null);
  const [tab, setTab] = React.useState('all');
  const [groupFilter, setGroupFilter] = React.useState<string>('all');
  const [groupOptions, setGroupOptions] = React.useState<string[]>([]);
  const [advancedFilters, setAdvancedFilters] = React.useState({
    groups: [] as string[],
    adminOnly: false,
    hasEmail: false,
    hasPhone: false,
  });
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState<Member | null>(null);

  const { data, isLoading: loadingMembers } = useMembersQuery({ page, pageSize, search });

  // Sync URL with state
  React.useEffect(() => {
    const sp = new URLSearchParams(params);
    sp.set('page', String(page));
    if (search) sp.set('q', search); else sp.delete('q');
    setParams(sp, { replace: true });
  }, [page, search, params, setParams]);

  // Debounce search input
  React.useEffect(() => {
    const h = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(h);
  }, [searchInput]);

  // Load members from Supabase
  React.useEffect(() => {
    setIsLoading(loadingMembers);
    if (data?.rows) {
      const mapped: Member[] = data.rows.map((r, idx) => {
        const fallbackEmail = `${(r.first_name || '').toLowerCase()}.${(r.last_name || '').toLowerCase()}@example.com`;
        return {
          id: (page - 1) * pageSize + idx + 1,
          name: `${r.first_name} ${r.last_name ?? ''}`.trim(),
          phone: r.phone || '',
          email: r.email || fallbackEmail,
          groups: r.groups || [],
          subgroups: [],
          isAdmin: r.isAdmin,
          dbUserId: r.user_id,
        };
      });

      devLog.log('Mapped members data:', mapped);
      setMembers(mapped);
    }
  }, [data, loadingMembers, page, pageSize]);

  // Load all groups for filter (once)
  React.useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data: allGroups, error } = await supabase.from('groups').select('name');
        if (error) {
          devLog.warn('Failed to load group options', error);
          return;
        }
        if (allGroups) {
          const opts = Array.from(new Set(allGroups.map((g: any) => g.name).filter(Boolean))).sort();
          setGroupOptions(opts);
        }
      } catch (error) {
        devLog.warn('Failed to load group options', error);
      }
    })();
  }, [supabase, user]);

  const filteredAll = members.filter((m) => {
    const matchesName = m.name.toLowerCase().includes(search.toLowerCase());
    const matchesGroup = groupFilter === 'all' ? true : m.groups.includes(groupFilter);
    
    // Advanced filters
    if (advancedFilters.adminOnly && !m.isAdmin) return false;
    if (advancedFilters.hasEmail && !m.email) return false;
    if (advancedFilters.hasPhone && !m.phone) return false;
    if (advancedFilters.groups.length > 0) {
      const hasAnyGroup = advancedFilters.groups.some(g => m.groups.includes(g));
      if (!hasAnyGroup) return false;
    }
    
    return matchesName && matchesGroup;
  });
  const filteredAdmins = filteredAll.filter((m) => m.isAdmin);

  // Calculate analytics data
  const groupDistribution = members.reduce((acc, m) => {
    m.groups.forEach(g => {
      acc[g] = (acc[g] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);

  const memberCount = data?.total ?? members.length;
  const adminCount = (data as any)?.adminCount ?? members.filter(m => m.isAdmin).length;
  const totalMembers = adminStats.totalUsers || memberCount;
  const totalAdmins = adminStats.adminCount || adminCount;
  const contactComplete = adminStats.contactComplete;
  const missingEmail = adminStats.missingEmail;
  const missingPhone = adminStats.missingPhone;
  const recentGrowth = {
    thisMonth: adminStats.usersThisMonth || 0,
    lastMonth: adminStats.usersThisMonth || 0,
  };

  // Start chat functionality
  const startChat = async (member: Member, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!member.dbUserId) {
      toast({
        title: t('common.error'),
        description: 'Cannot start chat: member ID not found',
        variant: 'destructive'
      });
      return;
    }
    if (!user) {
      toast({
        title: t('common.error'),
        description: 'You must be logged in to start a chat',
        variant: 'destructive'
      });
      return;
    }
    await handleStartChat(member.dbUserId, member.name);
  };

  // Member management functions
  const sanitizeGroups = (groups: string[]) => {
    const adminLabel = getLocalizedGroupName('Administratorer / Admins', language);
    return (groups || []).filter(g => g && g !== adminLabel && !/\badmin\b/i.test(g));
  };

  const ensureSessionOrMock = async () => Boolean(user && user.id);

  const createMutation = useMutation({
    mutationFn: async (memberData: { name: string; phone: string; email: string; isAdmin: boolean; groups: string[] }) => {
      const [first, ...rest] = memberData.name.split(' ');
      const last = rest.join(' ');
      const hasSession = await ensureSessionOrMock();
      if (!hasSession) {
        devLog.warn('manage-member create in mock mode; skipping remote call');
        return { data: { success: true, mock: true } } as any;
      }
      return supabase.functions.invoke('manage-member', {
        body: {
          action: 'create',
          email: memberData.email,
          first_name: first || memberData.name,
          last_name: last || null,
          phone: memberData.phone,
          isAdmin: memberData.isAdmin,
          groups: sanitizeGroups(memberData.groups),
        }
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['members'] }); },
    onError: (err: any) => {
      devLog.error('manage-member create failed:', err);
      toast({ title: t('common.error'), description: 'Could not create member. Please try again later.', variant: 'destructive' });
    },
  });

  const handleCreateMember = (memberData: {
    name: string;
    phone: string;
    email: string;
    groups: string[];
    subgroups: string[];
    isAdmin: boolean;
  }) => {
    const optimistic: Member = {
      id: Math.max(0, ...members.map(m => m.id)) + 1,
      name: memberData.name,
      phone: memberData.phone,
      email: memberData.email,
      groups: memberData.groups,
      subgroups: [],
      isAdmin: memberData.isAdmin,
    };
    setMembers(prev => [optimistic, ...prev]);
    createMutation.mutate({ name: memberData.name, phone: memberData.phone, email: memberData.email, isAdmin: memberData.isAdmin, groups: memberData.groups });
  };

  const updateMutation = useMutation({
    mutationFn: async (updated: Member) => {
      const [first, ...rest] = updated.name.split(' ');
      const last = rest.join(' ');
      const hasSession = await ensureSessionOrMock();
      if (!hasSession) {
        devLog.warn('manage-member update in mock mode; skipping remote call');
        return { data: { success: true, mock: true } } as any;
      }
      return supabase.functions.invoke('manage-member', {
        body: {
          action: 'update',
          user_id: updated.dbUserId,
          first_name: first || updated.name,
          last_name: last || null,
          phone: updated.phone,
          isAdmin: updated.isAdmin,
          groups: sanitizeGroups(updated.groups),
        }
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['members'] }); },
    onError: (err: any) => {
      devLog.error('manage-member update failed:', err);
      toast({ title: t('common.error'), description: 'Could not update member. Please try again later.', variant: 'destructive' });
    },
  });

  const handleUpdateMember = (updatedMember: Member) => {
    setMembers(prev => prev.map(m => m.id === updatedMember.id ? updatedMember : m));
    updateMutation.mutate(updatedMember);
  };

  const deleteMutation = useMutation({
    mutationFn: async (member: Member) => {
      if (!member.dbUserId) return;
      const hasSession = await ensureSessionOrMock();
      if (!hasSession) {
        devLog.warn('manage-member delete in mock mode; skipping remote call');
        return { data: { success: true, mock: true } } as any;
      }
      return supabase.functions.invoke('manage-member', { body: { action: 'delete', user_id: member.dbUserId } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['members'] }); },
    onError: (err: any) => {
      devLog.error('manage-member delete failed:', err);
      toast({ title: t('common.error'), description: 'Could not delete member. Please try again later.', variant: 'destructive' });
    },
  });

  const handleDeleteMember = (memberId: number) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;
    setMembers(prev => prev.filter(m => m.id !== memberId));
    deleteMutation.mutate(member);
  };

  const handleEditClick = (member: Member) => {
    setSelectedMember(member);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (member: Member) => {
    setSelectedMember(member);
    setDeleteDialogOpen(true);
  };

  const renderMemberCard = (member: Member) => (
    <Card key={member.id} className="overflow-hidden">
      <CardHeader
        className="cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(expanded === member.id ? null : member.id)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-5 w-5" />
            <div className="flex items-center gap-2">
              {member.name}
              {member.isAdmin && (
                <Badge variant="admin" className="text-xs">
                  {getLocalizedGroupName('Administratorer / Admins', language)}
                </Badge>
              )}
              {!member.isAdmin && member.groups[0] && (
                <Badge variant="outline" className="text-xs">
                  {member.groups[0]}
                </Badge>
              )}
            </div>
          </CardTitle>
          <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('messages.start_chat')}
                onClick={(e) => startChat(member, e)}
              >
              <MessageCircle className="h-4 w-4" />
            </Button>
            <ProtectedAdminRoute fallback={null}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('members.edit')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(member);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('members.delete')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(member);
                }}
              >
                <Trash className="h-4 w-4" />
              </Button>
            </ProtectedAdminRoute>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {member.groups.map((group) => {
            const colors = getGroupColorClasses(group);
            const localizedGroupName = getLocalizedGroupName(group, language);
            return (
              <Badge 
                key={group} 
                variant="outline" 
                className={cn("text-xs border-transparent", colors.bg, colors.text)}
              >
                {localizedGroupName}
              </Badge>
            );
          })}
        </div>
      </CardHeader>
      {expanded === member.id && (
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {member.email}
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            {member.phone}
          </div>
        </CardContent>
      )}
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="p-2">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t("nav.members")}</h1>
          <p className="text-sm text-muted-foreground">{t("members.subtitle")}</p>
        </div>
      </div>

      {/* Analytics Dashboard */}
      <ProtectedAdminRoute fallback={null}>
        <MemberAnalytics
          totalMembers={totalMembers}
          adminCount={totalAdmins}
          groupDistribution={groupDistribution}
          recentGrowth={recentGrowth}
          contactComplete={contactComplete}
          missingEmail={missingEmail}
          missingPhone={missingPhone}
        />
      </ProtectedAdminRoute>

      {/* Search, Filters and Actions */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder={t("placeholder.searchMembers")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="w-[180px]">
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="border rounded px-2 py-2 text-sm w-full"
            >
              <option value="all">{t('groups.filter.all_groups')}</option>
              {groupOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <ProtectedAdminRoute fallback={null}>
            <AdvancedMemberFilters
              filters={advancedFilters}
              onFiltersChange={setAdvancedFilters}
              availableGroups={groupOptions}
            />
            <MemberExport
              members={filteredAll.map(m => ({
                name: m.name,
                email: m.email,
                phone: m.phone,
                groups: m.groups,
                isAdmin: m.isAdmin,
              }))}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
              className="whitespace-nowrap"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t("members.add")}
            </Button>
          </ProtectedAdminRoute>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">{t("members.all")}</TabsTrigger>
          <TabsTrigger value="admins">{t("members.admins")}</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {isLoading ? (
            <SkeletonLoader variant="card" count={5} />
          ) : (
            <div className="space-y-4">
              {filteredAll.map(renderMemberCard)}
              {/* Pagination for DB-backed list */}
              <div className="flex items-center justify-between py-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>{t('common.prev')}</Button>
                <div className="text-xs text-muted-foreground">{t('common.page')} {page}</div>
                <Button variant="outline" size="sm" disabled={(data?.total ?? 0) <= page * pageSize} onClick={() => setPage(p => p + 1)}>{t('common.next')}</Button>
              </div>
              {filteredAll.length === 0 && (
                <p className="text-muted-foreground text-sm">{t("members.noMembers")}</p>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="admins">
          {isLoading ? (
            <SkeletonLoader variant="card" count={2} />
          ) : (
            <div className="space-y-4">
              {filteredAdmins.map(renderMemberCard)}
              {filteredAdmins.length === 0 && (
                <p className="text-muted-foreground text-sm">{t("members.noMembers")}</p>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreateMemberDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreateMember={handleCreateMember}
      />

      <EditMemberDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        member={selectedMember}
        onUpdateMember={handleUpdateMember}
      />

      <DeleteMemberDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        member={selectedMember}
        onDeleteMember={handleDeleteMember}
      />
    </div>
  );
}
